import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "./use-auth";

function getOrCreateSessionId(): string {
  const key = "fm_session_id";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

// Extracts article ID from a page view response if the path is an article page.
// We attach the articleId by reading the data-article-id meta tag that the
// article page sets on mount (see article.tsx), falling back to null.
function getArticleIdFromDom(): string | null {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="fm-article-id"]');
  return meta?.content ?? null;
}

export function usePageView() {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();
  const lastTracked = useRef<string>("");

  useEffect(() => {
    // Skip for authenticated (admin/staff) users
    if (isAuthenticated) return;

    // Skip if we've already tracked this exact path in this render cycle
    if (location === lastTracked.current) return;
    lastTracked.current = location;

    const sessionId = getOrCreateSessionId();

    // Small delay to allow the page to render and set meta tags
    const timer = setTimeout(() => {
      const articleId = getArticleIdFromDom();
      const payload: Record<string, unknown> = {
        path: location,
        sessionId,
        referrer: document.referrer || null,
      };
      if (articleId) payload.articleId = articleId;

      fetch("/api/analytics/page-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {}); // Fire-and-forget — never throw
    }, 500);

    return () => clearTimeout(timer);
  }, [location, isAuthenticated]);
}
